/**
 * React Hook for Data Source Adaptation
 * 
 * Provides reactive data transformation from external sources while
 * maintaining 100% backwards compatibility with Django endpoints.
 */

import { useMemo, useEffect, useState } from 'react';
import { 
  adaptDataSource, 
  isEndpointEditable, 
  toLegacyFormat 
} from '../utilities/dataSourceAdapters';
import { 
  StandardizedDataItem, 
  FieldMapping, 
  ApiEndpoint 
} from '../types';

/**
 * Hook to reactively adapt data from any source to standardized format
 * Now supports async adapters with proper loading states
 */
export const useDataSourceAdapter = (
  rawData: any,
  endpoint: ApiEndpoint | null,
  allEndpoints: ApiEndpoint[] = [], // Pass all endpoints for relationship mapping
  options: {
    returnLegacyFormat?: boolean; // Set to true for backwards compatibility
  } = {}
): { data: any[], loading: boolean, error: string | null } => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    let isCancelled = false;
    
    const adaptData = async () => {
      if (!rawData || !endpoint) {
        setData([]);
        setLoading(false);
        setError(null);
        return;
      }
      
      const { returnLegacyFormat = true } = options;
      
      setLoading(true);
      setError(null);
      
      try {
        // Determine source type - defaults to 'django' for backwards compatibility
        const sourceType = endpoint.dataSourceType || 'django';
        const fieldMapping = endpoint.dataSourceConfig?.fieldMapping;
        

        
        // Adapt the data - now async
        const standardizedData = await adaptDataSource(rawData, sourceType, fieldMapping, allEndpoints);
        
        if (isCancelled) return; // Don't update state if effect was cancelled
        
        // Convert back to legacy format for backwards compatibility
        if (returnLegacyFormat) {
          setData(standardizedData.map(item => toLegacyFormat(item)));
        } else {
          setData(standardizedData);
        }
      } catch (err) {
        if (isCancelled) return;
        
        console.error('Error adapting data source:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        
        // Fallback: return raw data for backwards compatibility
        if (Array.isArray(rawData)) {
          setData(rawData);
        } else if (rawData.results && Array.isArray(rawData.results)) {
          setData(rawData.results);
        } else {
          setData([rawData]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    
    adaptData();
    
    return () => {
      isCancelled = true;
    };
  }, [rawData, endpoint, allEndpoints, options.returnLegacyFormat]);
  
  return { data, loading, error };
};

/**
 * Hook to check if an endpoint is editable
 */
export const useIsEndpointEditable = (endpoint: ApiEndpoint | null): boolean => {
  return useMemo(() => {
    if (!endpoint) return false;
    return isEndpointEditable(endpoint);
  }, [endpoint]);
};

/**
 * Hook to get standardized item (for advanced use cases)
 * Now supports async adapters with proper loading states
 */
export const useStandardizedData = (
  rawData: any,
  endpoint: ApiEndpoint | null,
  allEndpoints: ApiEndpoint[] = []
): { data: StandardizedDataItem[], loading: boolean, error: string | null } => {
  const [data, setData] = useState<StandardizedDataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    let isCancelled = false;
    
    const adaptData = async () => {
      if (!rawData || !endpoint) {
        setData([]);
        setLoading(false);
        setError(null);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const sourceType = endpoint.dataSourceType || 'django';
        const fieldMapping = endpoint.dataSourceConfig?.fieldMapping;
        
        const standardizedData = await adaptDataSource(rawData, sourceType, fieldMapping, allEndpoints);
        
        if (isCancelled) return;
        
        setData(standardizedData);
      } catch (err) {
        if (isCancelled) return;
        
        console.error('Error getting standardized data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setData([]);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    
    adaptData();
    
    return () => {
      isCancelled = true;
    };
  }, [rawData, endpoint, allEndpoints]);
  
  return { data, loading, error };
}; 